import React, { useEffect, ReactNode, useCallback, useRef, useLayoutEffect, useState } from 'react';
import { useOutsideClick } from '../../hooks/useOutsideClick';
import { usePopupManager } from '../../contexts/PopupManagerContext';

interface PopupProps {
  id: string;
  visible: boolean;
  onClose: () => void;
  position: { x: number; y: number };
  children: ReactNode;
  closeOnDeselect?: boolean;
  closeOnOutsideClick?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

export const Popup: React.FC<PopupProps> = ({
  id,
  visible,
  onClose,
  position,
  children,
  closeOnDeselect = true,
  closeOnOutsideClick = true,
  className = '',
  style = {}
}) => {
  const { registerPopup, unregisterPopup } = usePopupManager();
  const [translateYOffset, setTranslateYOffset] = useState(0);
  
  // Store the latest onClose function in a ref to avoid dependency issues
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  
  // Stable callback that uses the ref
  const stableOnClose = useCallback(() => {
    onCloseRef.current();
  }, []);
  
  // Register this popup with the manager
  useEffect(() => {
    if (visible) {
      registerPopup({
        id,
        onClose: stableOnClose,
        closeOnDeselect,
        closeOnOutsideClick
      });
    }

    return () => {
      unregisterPopup(id);
    };
  }, [visible, id, stableOnClose, closeOnDeselect, closeOnOutsideClick, registerPopup, unregisterPopup]);

  // Handle outside clicks
  const popupRef = useOutsideClick<HTMLDivElement>(
    () => {
      if (closeOnOutsideClick) {
        stableOnClose();
      }
    },
    visible && closeOnOutsideClick
  );

  // Compute final transform by appending a vertical translate to keep the popup within viewport.
  const baseTransform = (style && style.transform) || 'translate(-50%, 0)';
  const composedTransform = `${baseTransform}${translateYOffset ? ` translateY(${translateYOffset}px)` : ''}`;

  const defaultStyle: React.CSSProperties = {
    position: 'fixed',
    left: position.x,
    top: position.y,
    zIndex: 60,
    ...style,
    transform: composedTransform,
  };

  // Measure and clamp vertically within the viewport (top/bottom) with an 8px margin.
  const recomputeVerticalClamp = useCallback(() => {
    const el = popupRef.current;
    if (!el) return;
    const margin = 8; // per UX spacing scale (multiples of 4px)

    // Reset offset to 0 before measuring to avoid compounding adjustments
    setTranslateYOffset(0);
    requestAnimationFrame(() => {
      const rect = el.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const maxTop = viewportHeight - margin - rect.height;
      const clampedTop = Math.min(Math.max(rect.top, margin), Math.max(maxTop, margin));
      const delta = Math.round(clampedTop - rect.top);
      if (delta !== 0) {
        setTranslateYOffset(delta);
      }
    });
  }, [popupRef]);

  // Recompute on mount/visibility/position/transform changes
  useLayoutEffect(() => {
    if (!visible) return;
    recomputeVerticalClamp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, position.x, position.y, baseTransform]);

  // Recompute on window resize
  useEffect(() => {
    if (!visible) return;
    const onResize = () => recomputeVerticalClamp();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [visible, recomputeVerticalClamp]);

  if (!visible) return null;

  return (
    <div
      ref={popupRef}
      className={`popup ${className}`}
      style={defaultStyle}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>
  );
};
